// pages/LegalPages/PrivacyPolicyPage.jsx
import React from "react";
import {
    LegalPage,
    LegalContainer,
    LegalTitle,
    LegalText,
    LegalSectionTitle,
    LegalList,
    LegalLink,
    LegalAddress,
    LegalSubtitle
} from "./legal.elements";

const PrivacyPolicyPage = () => {
    return (
        <LegalPage>
            <LegalContainer>
                <LegalTitle>The Clippn Privacy Policy</LegalTitle>

                <LegalText>
                    Thank you for choosing to be part of our community at Clippn INC (“Company”, “we”, “us”, or “our”).
                    We are
                    committed to protecting your personal information and your right to privacy. If you have any
                    questions or
                    concerns about our policy, or our practices with regards to your personal information, please
                    contact us via
                    the contact information provided at the end of this Privacy Policy.
                </LegalText>

                <LegalText>
                    When you visit our website{" "}
                    <LegalLink href="https://www.clippn.com" target="_blank" rel="noreferrer">
                        www.clippn.com
                    </LegalLink>
                    , and use our services, you trust us with your personal information. We take your privacy very
                    seriously. In
                    this privacy notice, we describe our privacy policy. We seek to explain to you in the clearest way
                    possible
                    what information we collect, how we use it, and what rights you have in relation to it. We hope you
                    take some
                    time to read through it carefully, as it is important. If there are any terms in this privacy policy
                    that you
                    do not agree with, please discontinue use of our Site(s) and our services.
                </LegalText>

                <LegalText>
                    This privacy policy applies to all information collected through our website,{" "}
                    <LegalLink href="https://www.clippn.com" target="_blank" rel="noreferrer">
                        www.clippn.com
                    </LegalLink>
                    , and or any related services, sales, marketing or events (we refer to them collectively in this
                    privacy policy
                    as the “Sites”).
                </LegalText>

                <LegalText>Please read this privacy policy carefully as it will help you make informed decisions about
                    sharing your personal information with us.</LegalText>

                <LegalSectionTitle>TABLE OF CONTENTS</LegalSectionTitle>
                <LegalList>
                    <li>WHAT INFORMATION DO WE COLLECT?</li>
                    <li>HOW DO WE USE YOUR INFORMATION?</li>
                    <li>WILL YOUR INFORMATION BE SHARED WITH ANYONE?</li>
                    <li>DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</li>
                    <li>IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?</li>
                    <li>HOW LONG DO WE KEEP YOUR INFORMATION?</li>
                    <li>HOW DO WE KEEP YOUR INFORMATION SAFE?</li>
                    <li>DO WE COLLECT INFORMATION FROM MINORS?</li>
                    <li>WHAT ARE YOUR PRIVACY RIGHTS?</li>
                    <li>DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</li>
                    <li>DO WE MAKE UPDATES TO THIS POLICY?</li>
                    <li>HOW CAN YOU CONTACT US ABOUT THIS POLICY?</li>
                </LegalList>

                <LegalSectionTitle>1. WHAT INFORMATION DO WE COLLECT?</LegalSectionTitle>

                <LegalText>
                    In short: We collect personal information that you provide to us such as name, address, contact
                    information,
                    passwords and security data, and payment information.
                </LegalText>

                <LegalText>
                    We collect personal information that you voluntarily provide to us when registering at the Sites
                    expressing an
                    interest in obtaining information about us or our products and services, when participating in the
                    activities
                    on the Sites or otherwise contacting us.
                </LegalText>

                <LegalText>
                    The personal information that we collect depends on the context of your interactions with us and the
                    Sites, the
                    choices you make and the products and features you use. The personal information we collect can
                    include the
                    following:
                </LegalText>

                <LegalText>
                    <strong>Name and Contact Data:</strong> We collect your first and last name, email address, postal
                    address,
                    phone number(s), and other similar contact data.
                </LegalText>

                <LegalText>
                    <strong>Credentials:</strong> We collect passwords, password hints, and similar security information
                    used for
                    authentication and account access.
                </LegalText>

                <LegalText>
                    <strong>Payment Data:</strong> We do not collect or process payment information in the
                    Clippn apps. There are no purchases in the app and no payment instrument is requested or stored.
                </LegalText>

                <LegalText>
                    All personal information that you provide to us must be true, complete and accurate, and you must
                    notify us of
                    any changes to such personal information.
                </LegalText>

                <LegalSectionTitle>Information automatically collected</LegalSectionTitle>

                <LegalText>
                    In Short: Some information – such as IP address and/or browser and device characteristics – is
                    collected
                    automatically when you visit our Sites.
                </LegalText>

                <LegalText>
                    We automatically collect certain information when you visit, use or navigate the Sites. This
                    information does
                    not reveal your specific identity (like your name or contact information) but may include device and
                    usage
                    information, such as your IP address, browser, and device characteristics, operating system,
                    language
                    preferences, referring URLs, device name, country, location, information about how and when you use
                    our Sites
                    and other technical information. This information is primarily needed to maintain the security and
                    operation of
                    our Sites, and for our internal analytics and reporting purposes.
                </LegalText>

                <LegalText>Like many businesses, we also collect information through cookies and similar
                    technologies.</LegalText>

                <LegalSectionTitle>Analytics and product telemetry</LegalSectionTitle>

                <LegalText>
                    We use PostHog, a product analytics service, to understand how our apps are used and to diagnose
                    failures — in particular why an upload stalls or fails. When you are signed in, these events are
                    linked to your account.
                </LegalText>

                <LegalText>
                    The events we send include upload lifecycle actions (started, paused, resumed, completed, cancelled,
                    failed, stalled), transfer measurements such as speed and duration, your connection type, and your
                    device model, operating system version and app version. We do not send the contents of your footage,
                    your location coordinates, or your password.
                </LegalText>

                <LegalText>
                    PostHog processes this data on our behalf as a data processor. We do not use it for advertising, we
                    do not sell it, and we do not combine it with data from other companies to track you across other
                    apps or websites.
                </LegalText>

                <LegalSectionTitle>Information collected from other sources</LegalSectionTitle>

                <LegalText>
                    In Short: We may collect limited data from public databases, marketing partners, and other outside
                    sources.
                </LegalText>

                <LegalText>
                    We may obtain information about you from other sources, such as public databases, joint marketing
                    partners, as
                    well as from other third parties. Examples of the information we receive from other sources include:
                    social
                    media profile information; marketing leads and search results and links, including paid listings
                    (such as
                    sponsored links).
                </LegalText>

                <LegalSectionTitle>2. HOW DO WE USE YOUR INFORMATION?</LegalSectionTitle>

                <LegalText>
                    In Short: We process your information for purposes based on legitimate business interests, the
                    fulfillment of
                    our contract with you, compliance with our legal obligations, and/or your consent.
                </LegalText>

                <LegalText>
                    We use personal information collected via our Sites for a variety of business purposes described
                    below. We
                    process your personal information for these purposes in reliance on our legitimate business
                    interests (“Business
                    Purposes”) in order to enter into or perform a contract with you (“Contractual”), with your consent
                    (“Consent”),
                    and/ or for compliance with our legal obligations (“Legal Reasons”). We indicate the specific
                    processing grounds
                    we rely on next to each purpose listed below.
                </LegalText>

                <LegalList>
                    <li>
                        To facilitate account creation and logon process with your Consent. If you choose to link your
                        account with
                        us to a third party account (such as a Google or Facebook account), we use the information you
                        allowed us to
                        collect from those third parties to facilitate account creation and logon process.
                    </li>
                    <li>
                        To send you marketing and promotional communications for Business Purposes and/ or with your
                        Consent. We and/
                        or our third party marketing partners may use the personal information you send to us for our
                        marketing
                        purposes, if this is in accordance with your marketing preferences. You can opt out of our
                        marketing emails
                        at any time (see “WHAT ARE YOUR PRIVACY RIGHTS” herein below).
                    </li>
                    <li>
                        To send administrative information to you for Business Purposes, Legal Reasons and/or possibly
                        for Contractual
                        purposes. We may use your personal information to send you product, service and new feature
                        information and/
                        or information about changes to our terms, conditions and policies.
                    </li>
                    <li>
                        Fulfill and manage your orders for Contractual reasons. We may use your information to fulfill
                        and manage
                        your orders, payments, returns, and exchanges made through the Sites.
                    </li>
                    <li>
                        Deliver targeted advertising to you for our Business Purposes and / or with your Consent. We may
                        use your
                        information to develop and display content and advertising (and work with third parties who do
                        so) tailored
                        to your interests and/ or location and to measure its effectiveness.
                    </li>
                    <li>
                        Request Feedback for our Business Purposes and/or with your Consent. We may use your information
                        to request
                        feedback and to contact you about your use of our Sites.
                    </li>
                    <li>
                        To protect our Sites for Business Purposes and for Legal Reasons. We may use your information as
                        part of our
                        efforts to keep our Sites safe and secure (for example, for fraud monitoring and prevention).
                    </li>
                    <li>To enforce our terms, conditions and policies for Business Purposes, Legal Reasons and/ or
                        possibly Contractual.
                    </li>
                    <li>
                        To respond to legal requests and prevent harm for Legal Reasons. If we receive a subpoena or
                        other legal
                        request, we may need to inspect the data we hold to determine how to respond.
                    </li>
                    <li>
                        For other Business Purposes. We may use your information for other Business Purposes, such as
                        data analysis,
                        identifying usage trends, determining the effectiveness of our promotional campaigns and to
                        evaluate and
                        improve our Sites, products, services, marketing and your experience.
                    </li>
                </LegalList>

                <LegalSectionTitle>3. WILL YOUR INFORMATION BE SHARED WITH ANYONE?</LegalSectionTitle>

                <LegalText>
                    In Short: We only share information with your consent, to comply with laws, to protect your rights,
                    or to fulfill
                    business obligations.
                </LegalText>

                <LegalText>We only share and disclose your information in the following situations:</LegalText>

                <LegalList>
                    <li>
                        <strong>Compliance with Laws:</strong> We may disclose your information where we are legally
                        required to do so
                        in order to comply with applicable law, governmental requests, a judicial proceeding, court
                        order, or legal
                        process, such as in response to a court order or a subpoena (including in response to public
                        authorities to
                        meet national security or law enforcement requirements).
                    </li>
                    <li>
                        <strong>Vital Interests and Legal Rights:</strong> We may disclose your information where we
                        believe it is
                        necessary to investigate, prevent, or take action regarding potential violations of our
                        policies, suspected
                        fraud, situations involving potential threats to the safety of any person and illegal
                        activities, or as
                        evidence in litigation in which we are involved.
                    </li>
                    <li>
                        <strong>Vendors, Consultants and Other Third-Party Service Providers:</strong> We may share your
                        data with
                        third party vendors, service providers, contractors, or agents who perform services for us or on
                        our behalf
                        and require access to such information to do that work. Examples include: payment processing,
                        data analysis,
                        email delivery, hosting services, customer service and marketing efforts. We may allow selected
                        third parties
                        to use tracking technology on the Sites, which will enable them to collect data about how you
                        interact with
                        the Sites over time. This information may be used to, among other things, analyze and track
                        data, determine
                        the popularity of certain content and better understand online activity. Unless described in
                        this Policy, we
                        do not share, sell, rent or trade any of your information with third parties for their
                        promotional purposes.
                    </li>
                    <li>
                        <strong>Business Transfers:</strong> We may share or transfer your information in connection
                        with, or during
                        negotiations of, any merger, sale of company assets, financing, or acquisition of all or a
                        portion of our
                        business to another company.
                    </li>
                    <li>
                        <strong>Affiliates:</strong> We may share your information with our affiliates, in which case we
                        will require
                        those affiliates to honor this privacy policy. Affiliates include our parent company and
                        subsidiaries, joint
                        venture partners or other companies that we control or that are under common control with us.
                    </li>
                    <li>
                        <strong>Business Partners:</strong> We may share your information with our business partners to
                        offer you
                        certain products, services or promotions.
                    </li>
                    <li>
                        <strong>With your Consent:</strong> We may disclose your personal information for any other
                        purpose with your
                        consent.
                    </li>
                </LegalList>

                <LegalSectionTitle>4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</LegalSectionTitle>

                <LegalText>
                    In Short: We may use cookies and other tracking technologies to collect and store your information.
                </LegalText>

                    <LegalText>
                        <strong>Analytics:</strong> PostHog, for product analytics and upload diagnostics, as described
                        in section 1.
                    </LegalText>

                <LegalText>
                    We may use cookies and similar tracking technologies (like web beacons and pixels) to access or
                    store
                    information. Specific information about how we use such technologies and how you can refuse certain
                    cookies is
                    set out in our Cookie Policy.
                </LegalText>

                <LegalSectionTitle>5. IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?</LegalSectionTitle>

                <LegalText>
                    In Short: We may transfer, store, and process your information in countries other than your own.
                </LegalText>

                <LegalText>
                    Our servers are located in the United States. If you are accessing our Sites from outside the United
                    States,
                    please be aware that your information may be transferred to, stored, and processed by us in our
                    facilities and
                    by those third parties with whom we may share your personal information, in the United States, and
                    other
                    countries.
                </LegalText>

                <LegalText>
                    If you are a resident in the European Economic Area, then these countries may not have data
                    protection or other
                    laws as comprehensive as those in your country. We will, however, take all necessary measures to
                    protect your
                    personal information in accordance with this privacy policy and applicable law.
                </LegalText>

                <LegalSectionTitle>Transfers out of the European Economic Area</LegalSectionTitle>

                <LegalText>
                    Where personal information is transferred out of the European Economic Area or the United Kingdom,
                    we rely on the European Commission's Standard Contractual Clauses, together with the additional
                    safeguards required by applicable law, to protect it. The EU-U.S. Privacy Shield Framework was
                    invalidated in 2020 and we do not rely on it.
                </LegalText>

                <LegalSectionTitle>6. HOW LONG DO WE KEEP YOUR INFORMATION?</LegalSectionTitle>

                <LegalText>
                    In Short: We keep your information for as long as necessary to fulfill the purposes outlined in this
                    privacy
                    policy unless otherwise required by law.
                </LegalText>

                <LegalText>
                    We will only keep your personal information for as long as it is necessary for the purposes set out
                    in this
                    privacy policy, unless a longer retention period is required or permitted by law (such as tax,
                    accounting or
                    other legal requirements). No purpose in this policy will require us keeping your personal
                    information for
                    longer than two (2) years past the termination of the user’s account.
                </LegalText>

                <LegalText>
                    When we have no ongoing legitimate business need to process your personal information, we will
                    either delete
                    or anonymize it, or if this is not possible (for example, because your personal information has been
                    stored
                    in backup archives), then we will securely store your personal information and isolate it from any
                    further
                    processing until deletion is possible.
                </LegalText>

                <LegalTitle>7. HOW DO WE KEEP YOUR INFORMATION SAFE?</LegalTitle>

                <LegalText>
                    <strong>In Short:</strong> We aim to protect your personal information through a system of
                    organizational and technical
                    security measures.
                </LegalText>

                <LegalText>
                    We have implemented appropriate technical and organizational security measures designed to protect
                    the security of any
                    personal information we process. However, please also remember that we cannot guarantee that the
                    internet itself is 100%
                    secure. Although we will do our best to protect your personal information, transmission of personal
                    information to and from
                    our Sites is at your own risk. You should only access the services within a secure environment.
                </LegalText>

                <LegalTitle>8. DO WE COLLECT INFORMATION FROM MINORS?</LegalTitle>

                <LegalText>
                    <strong>In Short:</strong> We do not knowingly collect data from or market to children under the 18
                    years of age.
                </LegalText>

                <LegalText>
                    We do not knowingly solicit data from or market to children under 18 years of age. By using the
                    Sites you represent that you
                    are at least 18 or that you are the parent or guardian of such a minor and consent to such minor
                    dependent’s use of the
                    Sites. If we learn that personal information from users less than 18 years of age has been collected
                    we will deactivate the
                    account associated with such users and we will take reasonable measures to promptly delete such data
                    from our records. If you
                    become aware of any data we have collected from children under the age of 18, please contact us
                    at{" "}
                    <LegalLink href="mailto:PRIVACY@clippn.com">PRIVACY@clippn.com</LegalLink>.
                </LegalText>

                <LegalTitle>9. WHAT ARE YOUR PRIVACY RIGHTS?</LegalTitle>

                <LegalText>
                    <strong>In Short:</strong> In some regions, such as the European Economic Area, you have rights that
                    allow you greater access
                    to and control over your personal information. You may review, change, or terminate your account at
                    any time.
                </LegalText>

                <LegalText>
                    In some regions (like the European Economic Area), you have certain rights under applicable data
                    protection laws. These may
                    include the right (i) to request access and obtain a copy of your personal information; (ii) to
                    request rectification or
                    erasure; (iii) to restrict the processing of your personal information; and (iv) if applicable, to
                    data portability. In
                    certain circumstances, you may also have the right to object to the processing of your personal
                    information. To make such a
                    request, please use the contact details provided below. We will consider and act upon any request in
                    accordance with
                    applicable data protection laws.
                </LegalText>

                <LegalText>
                    If we are relying on your consent to process your personal information, you have the right to
                    withdraw your consent at any
                    time. Please note however that this will not affect the lawfulness of the processing before its
                    withdrawal.
                </LegalText>

                <LegalText>
                    If you are resident in the European Economic Area and you believe we are unlawfully processing your
                    personal information, you
                    also have the right to complain to your local data protection supervisory authority. You can find
                    their contact details here:{" "}
                    <LegalLink
                        href="http://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm"
                        target="_blank"
                        rel="noreferrer"
                    >
                        http://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm
                    </LegalLink>
                </LegalText>

                <LegalSubtitle>Account Information</LegalSubtitle>

                <LegalText>
                    If you would at any time like to review or change the information in your account or terminate your
                    account in accordance with
                    the terms of the Contributor Agreement, you can:
                </LegalText>

                <LegalList>
                    <li>Contact us using the contact information provided.</li>
                    <li>Log into your account settings and update your user account.</li>
                </LegalList>

                <LegalText>
                    Upon your request to terminate your account, in accordance with the terms of the Contributor
                    Agreement, we will deactivate or
                    delete your account and information from our active databases. You are responsible to read and
                    understand your obligations and
                    rights under the Contributor Agreement. With regard to your information, please be aware that some
                    information may be retained
                    in our files to prevent fraud, troubleshoot problems, assist with any investigations, enforce our
                    Terms of Use and/or comply
                    with legal requirements.
                </LegalText>

                <LegalText>
                    Cookies and similar technologies: Most Web browsers are set to accept cookies by default. If you
                    prefer, you can usually
                    choose to set your browser to remove cookies and to reject cookies. If you choose to remove or
                    reject cookies, this could
                    affect certain features or services of our Sites. To opt-out of interest-based advertising by
                    advertisers on our Sites visit{" "}
                    <LegalLink href="http://www.aboutads.info/choices/" target="_blank" rel="noreferrer">
                        http://www.aboutads.info/choices/
                    </LegalLink>
                    .
                </LegalText>

                <LegalText>
                    Opting out of email marketing: You can unsubscribe from our marketing email list at any time by
                    clicking on the unsubscribe
                    link in the emails that we send or by contacting us using the details provided below. You will then
                    be removed from the
                    marketing email list – however, we still need to send you service-related emails that are necessary
                    for the administration and
                    use of your account. To otherwise opt-out, you may:
                </LegalText>

                <LegalList>
                    <li>Note your preferences when you register an account with the site.</li>
                    <li>Access your account settings and update preferences.</li>
                    <li>Contact us using the contact information provided.</li>
                </LegalList>

                <LegalTitle>10. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</LegalTitle>

                <LegalText>
                    <strong>In Short:</strong> Yes, if you are a resident of California, you are granted specific rights
                    regarding access to your
                    personal information
                </LegalText>

                <LegalText>
                    California Civil Code Section 1798.83, also known as the “Shine the Light” law, permits our users
                    who are California
                    residents to request and obtain from us, once a year and free of charge, information about
                    categories of personal information
                    (if any) we disclosed to third parties for direct marketing purposes and the names and addresses of
                    all third parties with
                    which we shared personal information in the immediately preceding calendar year. If you are a
                    California resident and would
                    like to make such a request, please submit your request in writing to us using the contact
                    information provided below.
                </LegalText>

                <LegalText>
                    If you are under 18 years of age, reside in California, and have a registered account with the
                    Sites, you have the right to
                    request removal of unwanted data that you publicly post on the Sites. To request removal of such
                    data, please contact us
                    using the contact information provided below and include the email address associated with your
                    account and a statement that
                    you reside in California. We will make sure the data is not publicly displayed on the Sites, but
                    please be aware that the data
                    may not be completely or comprehensively removed from our systems.
                </LegalText>

                <LegalTitle>11. DO WE MAKE UPDATES TO THIS POLICY?</LegalTitle>

                <LegalText>
                    <strong>In Short:</strong> Yes, we will update this policy as necessary to remain compliant with all
                    applicable, relevant
                    laws.
                </LegalText>

                <LegalText>
                    We may update this privacy policy from time to time. The updated version will be indicated by an
                    updated “Revised” date and
                    the update version will be effective as soon as it is accessible. If we make material changes to
                    this privacy policy, we may
                    notify you either by prominently posting a notice of such changes or by directly sending you a
                    notification. We encourage you
                    to review this privacy policy frequently to be informed of how we are protecting your information.
                </LegalText>

                <LegalTitle>12. HOW CAN YOU CONTACT US ABOUT THIS PRIVACY POLICY?</LegalTitle>

                <LegalText>
                    If you have questions or comments about this policy, you may contact our Data Protection Officer
                    (DPO), by email at{" "}
                    <LegalLink href="mailto:PRIVACY@clippn.com">PRIVACY@clippn.com</LegalLink>, or by regular mail to:
                </LegalText>

                <LegalAddress>
                    <div>CLIPPN INC</div>
                    <div>Attn: DATA PROTECTION OFFICER</div>
                    <div>135 E. 57th St.</div>
                    <div>New York, NY 10022</div>
                    <div>U.S.A.</div>
                </LegalAddress>

                <LegalText>
                    If you have any further questions or comments about us or about our policies, please email us
                    at{" "}
                    <LegalLink href="mailto:Feedback@clippn.com">Feedback@clippn.com</LegalLink>, or by regular mail to:
                </LegalText>

                <LegalAddress>
                    <div>CLIPPN INC</div>
                    <div>Attn: Feedback</div>
                    <div>135 E. 57th St.</div>
                    <div>New York, NY 10022</div>
                    <div>U.S.A.</div>
                </LegalAddress>

            </LegalContainer>
        </LegalPage>
    );
}

export default PrivacyPolicyPage;